import React from 'react';
import { ArrowLeft, X } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';

type TemplateFillModalProps = {
  open: boolean;
  templateName: string;
  placeholders: string[];
  signFields?: string[];
  loadingPlaceholders: boolean;
  loadError: string | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => Promise<void>;
};

function humanizePlaceholder(key: string): string {
  const normalized = key.startsWith('d.') ? key.slice(2) : key;
  return normalized
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function TemplateFillModal({
  open,
  templateName,
  placeholders,
  signFields = [],
  loadingPlaceholders,
  loadError,
  submitting,
  onClose,
  onSubmit,
}: TemplateFillModalProps) {
  const [values, setValues] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!open) return;
    const initial: Record<string, string> = {};
    for (const ph of placeholders) initial[ph] = '';
    setValues(initial);
  }, [open, placeholders]);

  if (!open) return null;

  const visiblePlaceholders = placeholders.filter((ph) => !signFields.includes(ph));
  const hasSignFields = signFields.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-app-surface md:items-center md:justify-center md:bg-black/40 md:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex flex-col w-full h-full md:h-auto md:bg-app-surface md:rounded-xl md:border md:border-app-border md:shadow-xl md:max-w-3xl md:max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Platzhalter ausfüllen"
      >
        {/* Header */}
        <div className="shrink-0 px-4 py-3 md:px-5 md:py-4 border-b border-app-border flex items-center gap-3">
          {/* Mobile: back button */}
          <button
            type="button"
            className="md:hidden p-1.5 -ml-1 rounded-lg text-app-text-muted hover:bg-app-hover"
            onClick={onClose}
            disabled={submitting}
            aria-label="Zurück"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-app-text">Vorlage ausfüllen</h2>
            <p className="text-xs text-app-text-subtle mt-0.5 truncate">{templateName}</p>
          </div>

          {/* Mobile: submit in header */}
          <button
            type="button"
            disabled={submitting || loadingPlaceholders || (visiblePlaceholders.length === 0 && !hasSignFields)}
            onClick={() => void onSubmit(values)}
            className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pipedrive-blue text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? <LoadingSpinner /> : null}
            Fertig
          </button>

          {/* Desktop: close button */}
          <button
            type="button"
            className="hidden md:flex w-8 h-8 rounded-md hover:bg-app-hover text-app-text-subtle items-center justify-center"
            onClick={onClose}
            disabled={submitting}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {hasSignFields ? (
          <div className="shrink-0 mx-4 md:mx-5 mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900 leading-relaxed">
            Diese Vorlage enthält ein Signaturfeld. Name und E-Mail des Unterzeichners gibst du nach dem PDF-Schritt ein.
          </div>
        ) : null}

        {/* Body */}
        <div className="app-scrollbar flex-1 overflow-y-auto overscroll-contain px-4 py-4 md:px-5">
          {loadingPlaceholders ? (
            <InlineLoadingState label="Platzhalter werden gelesen..." className="py-12" />
          ) : loadError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {loadError}
            </div>
          ) : visiblePlaceholders.length === 0 && !hasSignFields ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Keine Platzhalter im Format <code>{'{d.xxx}'}</code> in der Vorlage gefunden.
            </div>
          ) : visiblePlaceholders.length === 0 && hasSignFields ? (
            <p className="text-sm text-app-text-muted">
              Keine weiteren Platzhalter - du kannst direkt das PDF erstellen.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {visiblePlaceholders.map((ph) => (
                <label key={ph} className="block">
                  <span className="text-xs text-app-text-muted font-medium">{humanizePlaceholder(ph)}</span>
                  <span className="block text-xs text-app-text-subtle mb-1.5">{ph}</span>
                  <input
                    value={values[ph] || ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [ph]: e.target.value }))}
                    className="w-full border border-app-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
                    placeholder="Wert eingeben"
                  />
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer — desktop only */}
        <div className="hidden md:flex px-5 py-4 border-t border-app-border items-center justify-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="btn-pipedrive-secondary"
            disabled={submitting}
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={submitting || loadingPlaceholders || (visiblePlaceholders.length === 0 && !hasSignFields)}
            onClick={() => void onSubmit(values)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? <LoadingSpinner /> : null}
            Dokument fertigstellen
          </button>
        </div>
      </div>
    </div>
  );
}
